import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import {
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  IToggleComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective } from "@jsverse/transloco";
import { AccountSelectionService, isAmazonBusinessAvailable } from "@front/m19-services";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";

export interface SpCreateStrategySetupModel {
  strategyName: string;
  onlyB2B: boolean;
}

@Component({
  selector: "app-sp-create-strategy-setup",
  imports: [
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    ReactiveFormsModule,
    TranslocoDirective,
    IIconComponent,
    IToggleComponent,
    ITooltipDirective,
    FormField,
  ],
  template: ` <div class="flex flex-col gap-8 py-8" *transloco="let t">
    <div class="flex flex-col gap-1">
      <h4>{{ t("strategy-form.setup_title") }}</h4>
      <p class="type-body text-muted-foreground m-0">{{ t("strategy-form.setup_description") }}</p>
    </div>
    <IFormField class="content">
      <label iLabel>{{ t("sp-strategy-creation.strategy_name") }}</label>
      <input
        iInput
        [placeholder]="t('sp-strategy-creation.please_enter_a_name_for_your_strategy')"
        [formField]="setup().strategyName"
        data-testid="strategy-name"
      />
    </IFormField>
    @if (amazonBusinessAvailable()) {
      <div class="flex items-center gap-4">
        <div class="inline-flex items-center gap-1">
          <div class="text-sm font-medium tracking-tight text-gray-800">
            {{ t("sp-strategy-creation.amazon_business_only") }}
          </div>
          <IIcon
            name="infoCircle"
            class="size-4 text-gray-500"
            [iTooltip]="t('sp-strategy-creation.amazon_business_only_description')"
            iTooltipPlacement="bottom-center"
          />
        </div>
        <IToggle
          [checked]="setup().onlyB2B().value()"
          (checkedChange)="setup().onlyB2B().value.set($event)"
          testid="only-b2b"
        />
      </div>
    }
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpCreateStrategySetupComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);

  readonly setup = input.required<FieldTree<SpCreateStrategySetupModel>>();

  private readonly selectedAccount = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  amazonBusinessAvailable = computed(() => isAmazonBusinessAvailable(this.selectedAccount()?.marketplace));
}
